// Full-screen viewer (double-click / double-tap from the grid): zoom/pan/
// pinch/swipe navigation, prev/next peeks, pinch-to-dismiss. Fit is the hard
// zoom floor and every zoom/pan funnels through clampPan, so fit is always
// the centered resting state (sub-fit is reachable only via the
// pinch-to-dismiss gesture — see the pinchMode state machine). Closing is
// ✕ / Esc / dismiss only, never a click; chrome is mouse-only and auto-hides.
// Details live at each handler below.
import { cachedThumb } from './thumbs.js';
import { tapDist, toggleBrowserFullscreen, ptrType } from './util.js';
import { createSwipeTracker, runTransition, createTapDetector } from './gestures.js';
import { createPlayer } from './video.js';

// `cancelHoverPlay` (hoverplay.js) is passed in explicitly (see app.js wiring)
// so viewer.js never imports hoverplay.js directly: opening the viewer stops
// any grid hover-play still running in a cell (its video would otherwise keep
// playing and bleed over the viewer).
export function initViewer({ grid, viewer, cancelHoverPlay }) {
  const viewerStage = document.getElementById('viewerStage');
  const viewerClose = document.getElementById('viewerClose');
  const zoomBar = document.getElementById('zoomBar');
  const peekPrev = document.getElementById('peekPrev');
  const peekNext = document.getElementById('peekNext');
  let viewerImg = null, viewerIndex = -1, viewerPlayer = null;
  let vScale = 1, vTx = 0, vTy = 0, natW = 1, natH = 1, fitScale = 1;
  // Live swipe offset: added on top of the fit/zoom transform while a
  // finger-following drag is in progress at fit scale (or always, for video —
  // it has no zoom transform of its own). Zero outside of a drag. Axis-aligned
  // — only one of swTx/swTy is ever nonzero at a time.
  let swTx = 0, swTy = 0;
  const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
  const applyV = () => {
    if (viewerImg) viewerImg.style.transform = `translate(${vTx + swTx}px,${vTy + swTy}px) scale(${vScale})`;
    updatePeeks();
  };
  // Video has no zoom transform system of its own — the swipe offset is the
  // whole of its transform. Reuses the element cached at gesture start
  // (swipeEl, set below) rather than re-querying the DOM on every pointermove.
  function applySwipeOffset() {
    if (viewerImg) { applyV(); return; }
    if (swipeEl) swipeEl.style.transform = `translate(${swTx}px,${swTy}px)`;
    updatePeeks();
  }
  const mediaEl = () => viewerImg || viewerStage.querySelector('video');
  const currentCells = () => [...grid.children];

  // Object-URL cache of originals for prev/current/next — keeps switching instant.
  const vcache = new Map();   // name -> object URL
  async function ensureFile(item) {
    let url = vcache.get(item.name);
    if (url) return url;
    const url2 = URL.createObjectURL(await item.handle.getFile());
    vcache.set(item.name, url2);
    if (item.kind === 'image') { const im = new Image(); im.decoding = 'async'; im.src = url2; } // warm decode
    return url2;
  }
  function pruneCache(keep) {
    for (const [name, url] of vcache) if (!keep.has(name)) { URL.revokeObjectURL(url); vcache.delete(name); }
  }

  function fitImage() {
    const cw = viewerStage.clientWidth, ch = viewerStage.clientHeight;
    fitScale = Math.min(cw / natW, ch / natH);
    vScale = fitScale;
    vTx = (cw - natW * vScale) / 2;
    vTy = (ch - natH * vScale) / 2;
    applyV();
  }
  // Keep the image "glued" to its fit orientation (req 4): on each axis, if the
  // scaled media is smaller than the viewport it is perfectly centered; if it's
  // larger it may pan but never past its own edge (no empty gutter). At fit
  // scale both axes center, so fit is always the perfectly-centered resting
  // state. Every zoom/pan (buttons, wheel, pinch, arrows, mouse drag) funnels
  // through here so the invariant holds no matter how the transform got moved.
  function clampPan() {
    const cw = viewerStage.clientWidth, ch = viewerStage.clientHeight;
    const w = natW * vScale, h = natH * vScale;
    vTx = w <= cw ? (cw - w) / 2 : clamp(vTx, cw - w, 0);
    vTy = h <= ch ? (ch - h) / 2 : clamp(vTy, ch - h, 0);
  }
  // `f` toggles browser fullscreen (toggleBrowserFullscreen, shared with the
  // toolbar ⛶ / grid `F`): the viewer is already a full-screen overlay, but
  // true fullscreen also hides browser chrome. `f` used to mean fit-to-screen;
  // fit moved to `0`.
  // Zoom can never go below fit (req 3): fitScale is the hard floor. Going
  // below fit is exclusively the pinch-to-dismiss gesture, which bypasses
  // zoomAt entirely (see the pinch handler).
  function zoomAt(factor, px, py) {
    if (!viewerImg) return;
    const ns = clamp(vScale * factor, fitScale, 40);
    const ix = (px - vTx) / vScale, iy = (py - vTy) / vScale;
    vScale = ns; vTx = px - ix * vScale; vTy = py - iy * vScale;
    clampPan();
    applyV();
  }
  const zoomedIn = () => !!viewerImg && vScale > fitScale * 1.01;
  // Arrow-key / programmatic pan step, clamped to the fit-glue rules (req 6).
  function panBy(dx, dy) {
    if (!zoomedIn()) return;
    vTx += dx; vTy += dy; clampPan(); applyV();
  }

  function mediaRect() {
    if (viewerImg) return { l: vTx, t: vTy, r: vTx + natW * vScale, b: vTy + natH * vScale };
    const v = viewerStage.querySelector('video');
    if (v) { const r = v.getBoundingClientRect(); return { l: r.left, t: r.top, r: r.right, b: r.bottom }; }
    return null;
  }
  const overlapsMedia = (el, m) => {
    const r = el.getBoundingClientRect();
    return !!m && !(r.right < m.l || r.left > m.r || r.bottom < m.t || r.top > m.b);
  };
  // A peek with no neighbour is hidden outright (display:none). One that has a
  // neighbour is always in the DOM; when it would overlap the media it gets the
  // `.peek--dim` class, which CSS fades to a faint ghost — hovering the peek
  // then eases its opacity back to full via a CSS transition (no JS hover
  // tracking). opacity doesn't affect layout, so getBoundingClientRect still
  // measures the box without any unhide dance. The close ✕ and zoom bar get the
  // same overlap-dim treatment (`.ctl--dim`, req 2) so they fade over the image
  // and light back up on hover.
  function updatePeeks() {
    const m = mediaRect();
    for (const el of [peekPrev, peekNext]) {
      if (el.dataset.has !== '1') { el.hidden = true; continue; }
      el.hidden = false;
      el.classList.toggle('peek--dim', overlapsMedia(el, m));
    }
    viewerClose.classList.toggle('ctl--dim', overlapsMedia(viewerClose, m));
    if (!zoomBar.hidden) zoomBar.classList.toggle('ctl--dim', overlapsMedia(zoomBar, m));
  }
  // The chrome (close ✕, zoom bar, peeks) is a MOUSE-ONLY affordance: it shows
  // only while the mouse is the active input, and never on touch (touch
  // navigates by swipe and closes by pinch-to-dismiss). So the viewer opens with
  // chrome hidden; a mouse move reveals it and arms a short inactivity fade
  // (`showUi`); the fade, or any touch, hides it again (`hideUi`). Keyboard
  // never touches it. CSS transitions the opacity so it fades rather than pops,
  // and pointer-events:none while hidden means a stray mouse click on a
  // faded-out control just re-reveals it (via the preceding move) instead of
  // firing — and there are no touch-tappable controls to be locked out of,
  // since touch never shows them.
  // 2200ms in production; a test harness may lower it via the timing override
  // (see js/app.js's central definition). Unset in production → exactly 2200.
  const UI_HIDE_MS = globalThis.__MS_TEST_TIMINGS__?.uiHideMs ?? 2200;
  let uiHideT = null;
  function hideUi() { viewer.classList.add('hide-ui'); clearTimeout(uiHideT); }
  function showUi() {   // reveal + arm the inactivity fade (mouse only)
    viewer.classList.remove('hide-ui');
    clearTimeout(uiHideT);
    uiHideT = setTimeout(() => viewer.classList.add('hide-ui'), UI_HIDE_MS);
  }
  function setPeek(el, cell) {
    if (!cell) { el.dataset.has = '0'; el.hidden = true; return; }
    el.dataset.has = '1';
    const img = el.querySelector('img');
    cachedThumb(cell._item).then(u => { img.src = u; }).catch(() => {});
  }

  async function loadViewerMedia(item) {
    viewerPlayer?.destroy(); viewerPlayer = null;
    viewerStage.replaceChildren();
    viewerImg = null;
    const url = await ensureFile(item);
    if (item.kind === 'video') {
      zoomBar.hidden = true;
      const v = document.createElement('video');
      v.src = url; v.autoplay = true; v.playsInline = true;
      // No native video fullscreen — it would swallow Esc (the viewer is already full-screen).
      v.disablePictureInPicture = true;
      v.setAttribute('controlslist', 'nofullscreen noremoteplayback');
      v.addEventListener('loadedmetadata', updatePeeks);
      viewerStage.appendChild(v);
      // Custom player replaces native controls: sound ON (unlike
      // every muted preview context), tap-to-pause, scrub strip.
      viewerPlayer = createPlayer(viewerStage, v, { muted: false });
      updatePeeks();
    } else {
      zoomBar.hidden = false;
      const img = document.createElement('img');
      viewerImg = img;
      // Hide until fitImage has positioned+scaled it: the img is absolute /
      // transform-origin 0 0 / max-width:none, so a warm-decoded src would
      // otherwise paint one frame at full natural size pinned top-left (a
      // full-screen flash) before onload applies the fit transform.
      img.style.visibility = 'hidden';
      img.onload = () => { natW = img.naturalWidth || 1; natH = img.naturalHeight || 1; fitImage(); img.style.visibility = ''; };
      img.src = url;
      viewerStage.appendChild(img);
    }
  }

  async function showAt(idx) {
    const cells = currentCells();
    if (idx < 0 || idx >= cells.length) return;
    viewerIndex = idx;
    // Mirrors the grid cells' own dataset.name — an observable "what's showing"
    // hook (devtools, and the regression suite's directional-swipe assertions).
    viewerStage.dataset.name = cells[idx]._item.name;
    const keep = new Set();
    for (const di of [idx - 1, idx, idx + 1]) if (cells[di]) keep.add(cells[di]._item.name);
    pruneCache(keep);
    setPeek(peekPrev, cells[idx - 1]);
    setPeek(peekNext, cells[idx + 1]);
    await loadViewerMedia(cells[idx]._item);
    for (const di of [idx - 1, idx + 1]) if (cells[di]) ensureFile(cells[di]._item);   // preload neighbours
  }
  function openViewer(item) {
    if (!viewer.hidden) return;
    cancelHoverPlay?.();
    viewerOpenT = Date.now();
    viewer.hidden = false; viewer.classList.add('show');
    // Chrome is mouse-only: start hidden, and reveal immediately only if the
    // viewer was opened with the mouse (a mouse move keeps it up thereafter).
    hideUi();
    if (ptrType === 'mouse') showUi();
    const idx = currentCells().findIndex(c => c._item === item);
    showAt(idx >= 0 ? idx : 0);
  }
  function closeViewer() {
    if (viewer.hidden) return;
    clearTimeout(uiHideT);
    const v = viewerStage.querySelector('video'); if (v) { try { v.pause(); } catch {} }
    viewerPlayer?.destroy(); viewerPlayer = null;
    viewer.classList.remove('show', 'hide-ui'); viewer.hidden = true;
    viewer.style.background = ''; viewer.style.transition = '';
    viewerStage.replaceChildren();
    delete viewerStage.dataset.name;
    pruneCache(new Set());   // revoke everything
    viewerImg = null; vptrs.clear(); lastDist = 0; swTx = 0; swTy = 0; swipeTrack = null; swipeEl = null; swiping = false;
    pinchMode = 'none'; dismissRatio = 1;
    peekPrev.dataset.has = '0'; peekNext.dataset.has = '0';
    peekPrev.classList.remove('peek--dim'); peekNext.classList.remove('peek--dim');
    viewerClose.classList.remove('ctl--dim'); zoomBar.classList.remove('ctl--dim');
  }
  const viewerNext = () => showAt(viewerIndex + 1);
  const viewerPrev = () => showAt(viewerIndex - 1);

  // On touch, the browser synthesizes the click (and dblclick) of the opening tap
  // AFTER pointerup, hit-testing the screen again — by then the viewer covers the
  // tapped cell, so the ghost click would land on the stage and instantly close it
  // (or press a viewer button). Swallow clicks arriving right after opening. A
  // test harness may set `globalThis.__MS_TEST_TIMINGS__` (see js/app.js's
  // central comment) to shrink this below its 350ms production default.
  const GHOST_CLICK_MS = globalThis.__MS_TEST_TIMINGS__?.ghostClickMs ?? 350;
  let viewerOpenT = 0;
  for (const t of ['click', 'dblclick']) viewer.addEventListener(t, (e) => {
    if (Date.now() - viewerOpenT < GHOST_CLICK_MS) { e.stopPropagation(); e.preventDefault(); }
  }, true);

  document.getElementById('viewerClose').onclick = closeViewer;
  peekPrev.onclick = viewerPrev;
  peekNext.onclick = viewerNext;
  // No click-to-close on the stage: a click used to close the viewer, but the
  // compatibility 'click' fires after *every* pointerup (even a drag), so any
  // drag released over empty stage closed it. Closing is via ✕ / Esc only.
  // Mouse drag pans the media (see pointer handlers); a clean tap on video
  // pauses (createPlayer's tapToggle, bound on the stage).
  zoomBar.addEventListener('click', (e) => {
    const z = e.target.dataset.z; if (!z) return;
    if (z === 'fit') fitImage();
    else zoomAt(z === 'in' ? 1.25 : 0.8, viewerStage.clientWidth / 2, viewerStage.clientHeight / 2);
  });
  viewerStage.addEventListener('wheel', (e) => {
    if (!viewerImg) return;
    e.preventDefault();
    zoomAt(e.deltaY < 0 ? 1.15 : 1 / 1.15, e.clientX, e.clientY);
  }, { passive: false });
  viewerStage.addEventListener('dblclick', (e) => {
    if (!viewerImg) return;
    if (vScale > fitScale * 1.01) fitImage();
    else zoomAt(1 / fitScale, e.clientX, e.clientY);   // to 100%
  });

  // Pan (1 pointer, when zoomed — mouse or touch, clamped to the fit-glue
  // rules), pinch (2 pointers), tracked swipe-to-navigate (touch, 1 pointer, at
  // fit scale or on video — swipeTrack only gets created for those). At fit
  // scale the media follows the finger along whichever axis currently dominates
  // (swTx/swTy, axis-aligned — the other axis stays 0, re-decided each move so
  // an early wobble doesn't lock in the wrong one); on release a clearly
  // axis-aligned drag commits (step) in that direction, a diagonal one (or one
  // too short/slow) springs back. Zoomed-in panning writes straight into
  // vTx/vTy and is clamped so the media never leaves a gutter.
  const vptrs = new Map();
  let lastDist = 0, lastMidX = 0, lastMidY = 0, swiping = false, swipeTrack = null, swipeEl = null;
  // Pinch state machine (req 5). When a second finger lands at fit scale we
  // don't know yet whether the user means to zoom in or to dismiss, so we sit
  // in 'pending' — the media stays glued at fit — until the pinch distance
  // moves past PINCH_START in one direction, then commit to 'zoom' (expanding)
  // or 'dismiss' (pinching closed). A pinch that starts already zoomed in goes
  // straight to 'zoom'. In 'dismiss' the media shrinks below fit and the
  // backdrop fades; releasing past DISMISS_CLOSE closes the viewer, else it
  // springs back to fit. dismissRatio tracks the live pinch ratio for the
  // release decision.
  const PINCH_START = 0.06;    // ratio change from fit needed to pick a direction
  const DISMISS_CLOSE = 0.28;  // shrink (1 - ratio) past which release closes
  let pinchMode = 'none', pinchStartDist = 0, dismissRatio = 1;
  const twoP = () => {
    const [a, b] = [...vptrs.values()];
    return { d: Math.hypot(a.x - b.x, a.y - b.y), mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2 };
  };
  function resetSwipeOffset() {
    if (swTx || swTy) { swTx = 0; swTy = 0; applySwipeOffset(); }
  }
  // Center-scale the current media (image or video) to `ratio` of its fit size
  // with the given opacity. Images drive the vScale transform (origin 0,0, so
  // we recentre by hand); video has no zoom system, so a plain center-origin
  // `scale()` on the element does it (the video is flex-centred in the stage).
  function setDismissTransform(ratio, opacity) {
    if (viewerImg) {
      vScale = fitScale * ratio;
      const cw = viewerStage.clientWidth, ch = viewerStage.clientHeight;
      vTx = (cw - natW * vScale) / 2; vTy = (ch - natH * vScale) / 2;
      viewerImg.style.opacity = opacity;
      applyV();
    } else {
      const v = viewerStage.querySelector('video');
      if (v) { v.style.transformOrigin = 'center'; v.style.transform = `scale(${ratio})`; v.style.opacity = opacity; }
      updatePeeks();
    }
  }
  // Dismiss gesture visuals (req 5): center-scale the media below fit and fade
  // the backdrop as the pinch closes — works for both images and video.
  // `ratio` is the live pinch distance / start distance (< 1 while closing).
  // The backdrop keeps some opacity at the close threshold (`* 0.7`) so it
  // doesn't vanish before you've committed; finalizeDismiss fades it the rest
  // of the way out only once you actually release into a close.
  function applyDismiss(ratio) {
    dismissRatio = clamp(ratio, 0.2, 1);
    const t = clamp((1 - dismissRatio) / DISMISS_CLOSE, 0, 1);   // 0 at fit → 1 at close threshold
    viewer.style.background = `rgba(0,0,0,${(0.97 * (1 - t * 0.7)).toFixed(3)})`;
    setDismissTransform(dismissRatio, String(clamp(1 - t * 0.4, 0.6, 1)));
  }
  // Release decision for the dismiss gesture: closed far enough → animate the
  // rest of the way out and close; otherwise spring back to a clean fit.
  function finalizeDismiss() {
    const el = mediaEl();
    if (1 - dismissRatio >= DISMISS_CLOSE) {
      viewer.style.transition = 'background .18s ease';
      viewer.style.background = 'rgba(0,0,0,0)';
      runTransition(el, 'transform .18s ease, opacity .18s ease', () => {
        setDismissTransform(0.5, '0');
      }, () => closeViewer());
    } else {
      viewer.style.transition = 'background .16s ease';
      viewer.style.background = 'rgba(0,0,0,.97)';
      runTransition(el, 'transform .16s ease, opacity .16s ease', () => {
        if (viewerImg) { fitImage(); viewerImg.style.opacity = '1'; }
        else { const v = viewerStage.querySelector('video'); if (v) { v.style.transform = 'scale(1)'; v.style.opacity = '1'; } updatePeeks(); }
      }, () => { if (el) el.style.transition = ''; viewer.style.transition = ''; });
    }
  }
  // Mouse movement reveals the chrome and rearms its fade; any touch hides it
  // again (switching to touch means the mouse-only chrome should go away).
  viewer.addEventListener('pointermove', (e) => { if (e.pointerType === 'mouse') showUi(); });
  viewer.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse') hideUi(); });
  viewerStage.addEventListener('pointerdown', (e) => {
    // try/catch: synthetic test pointers have no active pointer to capture
    try { viewerStage.setPointerCapture(e.pointerId); } catch {}
    vptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (vptrs.size === 1) {
      swiping = true;
      const atFit = !viewerImg || vScale <= fitScale * 1.01;
      // Only touch gets the axis-locked swipe-to-navigate at fit. A mouse drag
      // at fit has nowhere to pan (the media exactly fits) so it does nothing;
      // a mouse drag when zoomed in falls through to the clamped pan branch.
      if (atFit && e.pointerType !== 'mouse') {
        swipeEl = mediaEl();   // cached once — move/commit/spring-back all reuse it, no repeated querySelector
        if (swipeEl) swipeEl.style.transition = 'none';   // live tracking must not animate
        swipeTrack = createSwipeTracker({ axisLock: true });   // no diagonal drag/commit
        swipeTrack.start(e.clientX, e.clientY);
      }
    } else {
      // A second finger down hands off to pinch — cancel any pending swipe
      // cleanly rather than letting a stale offset linger. Reset the live
      // offset *before* dropping swipeEl: for video, applySwipeOffset needs
      // swipeEl to actually clear the element's translate (an image clears via
      // applyV/viewerImg regardless), otherwise a swipe-then-pinch that stays
      // sub-threshold could leave the video parked off-centre.
      swiping = false; swipeTrack = null;
      resetSwipeOffset();
      swipeEl = null;
      const p = twoP(); lastDist = p.d; lastMidX = p.mx; lastMidY = p.my;
      // Decide the pinch intent: already zoomed in → straight to zoom; at fit
      // (image) or on video → wait to see which way the pinch goes (req 5).
      // Video can only ever dismiss (no zoom), but it still uses 'pending' so a
      // pinch-open on it is a no-op rather than an accidental dismiss.
      pinchMode = zoomedIn() ? 'zoom' : 'pending'; pinchStartDist = p.d; dismissRatio = 1;
    }
  });
  viewerStage.addEventListener('pointermove', (e) => {
    const prev = vptrs.get(e.pointerId); if (!prev) return;
    const dx = e.clientX - prev.x, dy = e.clientY - prev.y;
    vptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (vptrs.size >= 2) {
      const p = twoP();
      if (pinchMode === 'pending') {
        const ratio = p.d / pinchStartDist;
        if (ratio > 1 + PINCH_START) {
          if (!viewerImg) return;   // video: pinch-open does nothing (no zoom)
          // Commit to zoom; rebase the incremental tracking to now so the first
          // zoom frame doesn't jump by the start threshold.
          pinchMode = 'zoom'; lastDist = p.d; lastMidX = p.mx; lastMidY = p.my; return;
        }
        if (ratio < 1 - PINCH_START) pinchMode = 'dismiss';
        else return;   // undecided — media stays glued at fit
      }
      if (pinchMode === 'dismiss') {
        applyDismiss(p.d / pinchStartDist);
      } else if (viewerImg && lastDist) {
        // Zoom anchored at the pinch midpoint, following the midpoint as the
        // fingers travel; clampPan re-glues to the fit rules afterwards.
        const factor = p.d / lastDist;
        const ns = clamp(vScale * factor, fitScale, 40);
        const ix = (p.mx - vTx) / vScale, iy = (p.my - vTy) / vScale;
        vScale = ns;
        vTx = p.mx - ix * vScale + (p.mx - lastMidX);
        vTy = p.my - iy * vScale + (p.my - lastMidY);
        clampPan(); applyV();
      }
      lastDist = p.d; lastMidX = p.mx; lastMidY = p.my;
    } else if (swipeTrack) {
      const r = swipeTrack.move(e.clientX, e.clientY);
      swTx = r.dx; swTy = r.dy;
      applySwipeOffset();
    } else if (viewerImg && vScale > fitScale * 1.01) {
      vTx += dx; vTy += dy; clampPan(); applyV();     // pan only when zoomed in
    }
  });
  // sign > 0 (dragged right/down) steps to the previous item, < 0 to the next
  // — same convention as the side panel; vertical follows the same rule so
  // up (dy < 0) = next, down (dy > 0) = prev, per the fullscreen-viewer swipe
  // decision (nothing dismisses on swipe, only navigates).
  function commitSwipe(axis, sign) {
    const goNext = sign < 0;
    // No neighbour in that direction (first/last item) — spring back instead
    // of sliding the current media off-stage into a dead end.
    if (!currentCells()[viewerIndex + (goNext ? 1 : -1)]) { springBackSwipe(); return; }
    if (!swipeEl) { goNext ? viewerNext() : viewerPrev(); return; }
    const w = viewerStage.clientWidth, h = viewerStage.clientHeight;
    // Slide the current media the rest of the way off-stage (originals are
    // preloaded for images so this is cheap; for video it's a quick fade of
    // the same transform — no separate code path needed) then swap.
    runTransition(swipeEl, 'transform .14s ease, opacity .14s ease', () => {
      swTx = axis === 'x' ? sign * w : 0;
      swTy = axis === 'y' ? sign * h : 0;
      swipeEl.style.opacity = '0';
      applySwipeOffset();
    }, () => {
      swTx = 0; swTy = 0;   // the freshly-loaded media starts neutral, no transition
      goNext ? viewerNext() : viewerPrev();
    });
  }
  function springBackSwipe() {
    const el = swipeEl;
    runTransition(el, 'transform .15s ease', () => {
      swTx = 0; swTy = 0;
      applySwipeOffset();
    }, () => {
      if (el) el.style.transition = '';
    });
  }
  function endPointer(e) {
    vptrs.delete(e.pointerId);
    if (vptrs.size < 2) {
      lastDist = 0;
      // A finger lifting out of a dismiss pinch finalizes it (close or spring
      // back), regardless of whether one finger lingers.
      if (pinchMode === 'dismiss') finalizeDismiss();
      if (pinchMode !== 'none') pinchMode = 'none';
    }
    if (swiping && vptrs.size === 0 && swipeTrack) {
      const r = swipeTrack.end(e.clientX, e.clientY);
      if (r.commit) commitSwipe(r.axis, r.sign);   // reads swipeEl async — don't null it here
      else springBackSwipe();
      swipeTrack = null;
    }
    if (vptrs.size === 0) swiping = false;
  }
  viewerStage.addEventListener('pointerup', endPointer);
  viewerStage.addEventListener('pointercancel', endPointer);
  addEventListener('resize', () => { if (!viewer.hidden) { viewerImg ? fitImage() : updatePeeks(); } });

  // Viewer keys — capture phase so Esc/arrows win even when a video control is
  // focused. When zoomed in, the arrows pan the image (clamped to the fit-glue
  // rules, req 6) instead of navigating; at fit, ←/→ navigate.
  const PAN_STEP = 120;
  addEventListener('keydown', (e) => {
    if (viewer.hidden) return;
    if (e.key === 'Escape') closeViewer();
    else if (e.key === 'ArrowLeft') zoomedIn() ? panBy(PAN_STEP, 0) : viewerPrev();
    else if (e.key === 'ArrowRight') zoomedIn() ? panBy(-PAN_STEP, 0) : viewerNext();
    else if (e.key === 'ArrowUp') { if (!zoomedIn()) return; panBy(0, PAN_STEP); }
    else if (e.key === 'ArrowDown') { if (!zoomedIn()) return; panBy(0, -PAN_STEP); }
    else if (e.key === '+' || e.key === '=') zoomAt(1.25, innerWidth / 2, innerHeight / 2);
    else if (e.key === '-' || e.key === '_') zoomAt(0.8, innerWidth / 2, innerHeight / 2);
    else if (e.key === 'f' || e.key === 'F') toggleBrowserFullscreen();
    else if (e.key === '0' || e.key === ')') fitImage();
    else if (e.key === ' ') viewerPlayer?.toggle();
    else return;
    e.preventDefault(); e.stopPropagation();
  }, true);

  // Double-click / double-tap on a grid cell opens it. Capture phase = robust on
  // touch. Like the side panel, both taps must land close together (cells are large
  // at low column counts).
  const detectTap = createTapDetector();
  addEventListener('pointerup', (e) => {
    if (!viewer.hidden || document.body.classList.contains('dragging')) return;
    // A long-press range-select lift must not count as a tap — reset the
    // detector so a prior single tap can't pair with it into a double.
    if (document.body.classList.contains('range-selecting')) { detectTap(e, null); return; }
    const cell = e.target.closest && e.target.closest('.cell');
    if (!cell || !grid.contains(cell)) return;
    // Count as a tap unless the finger really travelled (touch taps jitter well past 10px).
    if (tapDist(e) > 30) { detectTap(e, null); return; }
    if (detectTap(e, cell) === 'double') {
      openViewer(cell._item);
    }
  }, true);
  // Native dblclick as a fallback (fires on mouse and many touch browsers).
  grid.addEventListener('dblclick', (e) => {
    const cell = e.target.closest('.cell');
    if (cell) openViewer(cell._item);
  });

  return { open: openViewer };
}
